import { ListViewControls } from "@/components/tables/ListViewControls";
import type { ListView } from "@/hooks/useListView";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import {
    Badge,
    Button,
    DataTable,
    Input,
    Mono,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Text,
    cn,
} from "@athena/ui";
import type { CrisisSeverity } from "@athena/common/graphql/generated/schema";
import type { CrisisResourcesQuery } from "../../graphql/crisis.generated";

type CrisisResource = CrisisResourcesQuery["crisisResources"][number];
type CrisisKeyword = CrisisResource["keywords"][number];
type CrisisEntry = { keyword: CrisisKeyword; resource: CrisisResource };

const SEVERITY_META: Record<CrisisSeverity, { label: string; className: string }> = {
    HIGH: {
        label: "High",
        className: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
    },
    MEDIUM: {
        label: "Medium",
        className:
            "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
    },
    LOW: {
        label: "Low",
        className: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300",
    },
};

const SeverityBadge = ({ severity }: { severity: CrisisSeverity }) => {
    const meta = SEVERITY_META[severity];
    return (
        <Badge variant="outline" className={cn("h-5 px-2 text-[11px] font-medium", meta.className)}>
            {meta.label}
        </Badge>
    );
};

interface CrisisWordsProps {
    search: string;
    onSearchChange: (value: string) => void;
    severityFilter: "all" | CrisisSeverity;
    onSeverityFilterChange: (value: "all" | CrisisSeverity) => void;
    view: ListView<CrisisEntry>;
    resultsLabel: string;
    selected: Set<string>;
    onEdit: (keyword: CrisisKeyword) => void;
    onDelete: (keyword: CrisisKeyword) => void;
}

export function CrisisWords({
    search,
    onSearchChange,
    severityFilter,
    onSeverityFilterChange,
    view,
    resultsLabel,
    selected,
    onEdit,
    onDelete,
}: CrisisWordsProps) {
    return (
        <>
            {/* Filters */}
            <div className="mb-4 flex items-center gap-3">
                <div className="relative max-w-xs flex-1">
                    <AlertTriangle className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search keywords..."
                        className="bg-card h-9 pl-9 text-sm"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                </div>

                <Select
                    value={severityFilter}
                    onValueChange={(v) => onSeverityFilterChange(v as typeof severityFilter)}
                >
                    <SelectTrigger className="bg-card h-9 w-36 text-sm">
                        <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                        <SelectItem value="all">All severities</SelectItem>
                        <SelectItem value="HIGH">High</SelectItem>
                        <SelectItem value="MEDIUM">Medium</SelectItem>
                        <SelectItem value="LOW">Low</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            {/* Above the table, where Becca asked for it: "the top of the table row or above it". */}
            <ListViewControls view={view} noun="keywords" count={resultsLabel} />

            <DataTable
                columns={[
                    {
                        key: "keyword",
                        header: "Keyword",
                        cell: ({ keyword }) => (
                            <Text weight="medium" className="text-sm">
                                {keyword.value}
                            </Text>
                        ),
                    },
                    {
                        key: "severity",
                        header: "Severity",
                        cell: ({ keyword }) => <SeverityBadge severity={keyword.severity} />,
                    },
                    {
                        key: "resource",
                        header: "Linked Resource",
                        cell: ({ resource }) => (
                            <div className="flex flex-col gap-0.5">
                                <Text className="text-sm">{resource.message}</Text>
                                <Mono size="xs" muted>
                                    {resource.phoneNumber && `${resource.phoneNumber} · `}
                                    {resource.email ?? "No contact info"}
                                </Mono>
                            </div>
                        ),
                    },
                ]}
                rows={view.rows}
                rowKey={({ keyword }) => keyword.id}
                rowSelected={({ keyword }) => selected.has(keyword.id)}
                filtered={!!search}
                empty={
                    <div className="py-12 text-center">
                        <Text muted className="text-sm">
                            No keywords yet. Add one now!
                        </Text>
                    </div>
                }
                noMatches="No keywords match your filters."
                actions={({ keyword }) => (
                    <div className="flex items-center justify-end gap-1">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={`Edit ${keyword.value}`}
                            onClick={() => onEdit(keyword)}
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${keyword.value}`}
                            onClick={() => onDelete(keyword)}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            />

            <div className="mt-4">
                <Mono className="text-muted-foreground text-xs">
                    {selected.size > 0 ? `${selected.size} selected` : ""}
                </Mono>
            </div>
        </>
    );
}
